import { createAsyncThunk } from "@reduxjs/toolkit";

import { getUserProfileRequest } from "Api/accountsProfile/getUserProfile.request";
import { getAuthUserRequest } from "Api/authUser/getAuthUser.request";
import logger from "Libs/logger";
import { setUserAttributesDrift } from "Reducers/app";
import { selectCurrentUserUUID } from "Reducers/app/selectors";
import { setThemeFromProfile } from "Reducers/app/theme";

import type { AccountsProfile } from "Api/accountsProfile/types";
import type { AuthUser } from "Api/authUser/types";
import type { AsyncThunkOptionType } from "Reducers/types";

export const loadUserProfile = createAsyncThunk<
  AccountsProfile & AuthUser,
  string | undefined,
  AsyncThunkOptionType
>(
  "app/profile/load",
  async (userId, { rejectWithValue, dispatch, getState }) => {
    // load a user by id or load the current user
    const id = userId || selectCurrentUserUUID(getState());
    if (!id) {
      return rejectWithValue("No id found");
    }

    let accountsProfile: AccountsProfile | null = null;
    let authUser: AuthUser | null = null;
    let accountsProfileError: Error | null = null;
    let authUserError: Error | null = null;

    // Try to get accounts profile
    try {
      accountsProfile = await getUserProfileRequest(id);
    } catch (error) {
      accountsProfileError = error as Error;
      logger(error as Error, {
        action: "LOAD_ACCOUNTS_PROFILE_FAILURE"
      });
    }

    // Try to get auth user
    try {
      authUser = await getAuthUserRequest(id);
      if (authUser) {
        setUserAttributesDrift({ user: authUser });
      }
    } catch (error) {
      authUserError = error as Error;
      logger(error as Error, {
        action: "LOAD_AUTH_USER_FAILURE"
      });
    }

    // If both failed, reject
    if (!accountsProfile && !authUser) {
      return rejectWithValue({
        accountsProfileError,
        authUserError,
        message: "Failed to load both user profile and auth user data"
      });
    }

    // Build profile from available data
    const profile = {} as AccountsProfile & AuthUser;

    // If we have accounts profile data, use it
    if (accountsProfile) {
      Object.assign(profile, accountsProfile);
    }

    // If we have auth user data, merge it
    if (authUser) {
      Object.assign(profile, authUser);

      // Set display_name and username from authUser. Filter the empty parts so
      // a user with no first/last name gets "" rather than a whitespace-only name.
      profile.display_name = [authUser.first_name, authUser.last_name]
        .filter(Boolean)
        .join(" ");
      profile.username = authUser.username;
    }

    dispatch(setThemeFromProfile({ profile }));
    return profile;
  }
);
